import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { app } from '../firebase';
import { 
  getFirestore, collection, orderBy, query, getDocs, addDoc, updateDoc, Timestamp, deleteDoc, doc 
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

function ClientComp() {
  const [clientsList, setClientsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null); // track if editing
  const [formData, setFormData] = useState({
    full_name: '',
    businessName: '',
    userphone: '',
    city: '',
    aboutBusiness: ''
  });

  const db = getFirestore(app);

  const fetchClients = async () => {
    const q = query(collection(db, 'clients'), orderBy('signupdate', 'desc'));
    const snapshot = await getDocs(q);
    const clients = [];
    snapshot.forEach(doc => clients.push({ id: doc.id, ...doc.data() }));
    setClientsList(clients);
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        // Update client
        await updateDoc(doc(db, 'clients', editingId), {
          ...formData,
          lastTimeLocationUpdate: Timestamp.now()
        });
        alert('Client updated successfully');
      } else {
        // Add new client
        await addDoc(collection(db, 'clients'), {
          ...formData,
          signupdate: Timestamp.now(),
          lastTimeLocationUpdate: Timestamp.now()
        });
        alert('Client added successfully');
      }

      setFormData({
        full_name: '',
        businessName: '',
        userphone: '',
        city: '',
        aboutBusiness: ''
      });
      setEditingId(null);
      setShowModal(false);
      await fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this client?')) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'clients', id));
      await fetchClients();
      alert('Client removed successfully');
    } catch (error) {
      console.error('Error removing client:', error);
      alert(error.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (client) => {
    setFormData({
      full_name: client.full_name || '',
      businessName: client.businessName || '',
      userphone: client.userphone || '',
      city: client.city || '',
      aboutBusiness: client.aboutBusiness || ''
    });
    setEditingId(client.id);
    setShowModal(true);
  };

  const navigate = useNavigate();

  function viewCustomer(clientId) {
    navigate(`/client-orders/${clientId}`);
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredClients = clientsList.filter((client) => {
    if (!normalizedSearch) return true;
    const values = [
      client.full_name,
      client.businessName,
      client.city,
      client.userphone,
      client.aboutBusiness
    ];
    return values.some((value) =>
      String(value || '').toLowerCase().includes(normalizedSearch)
    );
  });

  return (
    <div className="dataComp">
      <div className="dataCompTop">
        <h5>All My Current Clients</h5>
        <div className="searchContainer">
          <FontAwesomeIcon icon={faSearch} className="searchIcon" />
          <input
            type="text"
            placeholder="Type to search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button>Go</button>
          <button onClick={() => { setEditingId(null); setShowModal(true); }}>
            Add a Client
          </button>
        </div>
      </div>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Full Name</th>
            <th>Business Name</th>
            <th>Location</th>
            <th>Phone Number</th>
            <th>Sign Up date</th>
            <th>Last Active</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredClients.map(client => (
            <tr key={client.id}>
              <td>{client.full_name}</td>
              <td>{client.businessName}</td>
              <td>{client.city}</td>
              <td>{client.userphone}</td>
              <td>{client.signupdate?.toDate().toLocaleString()}</td>
              <td>{client.lastTimeLocationUpdate?.toDate().toLocaleString()}</td>
              <td>
                <button 
                  className="btn-sm btn btn-warning me-2" 
                  onClick={() => viewCustomer(client.id)}
                >
                  View Orders
                </button>
                <button 
                  className="btn-sm btn btn-info me-2" 
                  onClick={() => handleEdit(client)}
                >
                  Edit
                </button>
                <button 
                  className="btn-sm btn btn-danger" 
                  onClick={() => handleDelete(client.id)} 
                  disabled={deletingId === client.id}
                >
                  {deletingId === client.id ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Removing...
                    </>
                  ) : (
                    'Delete Client'
                  )}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Add/Edit Client Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Edit Client' : 'Add New Client'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control name="full_name" value={formData.full_name} onChange={handleChange} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Business Name</Form.Label>
              <Form.Control name="businessName" value={formData.businessName} onChange={handleChange} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control name="userphone" value={formData.userphone} onChange={handleChange} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>City</Form.Label>
              <Form.Control name="city" value={formData.city} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>About Business</Form.Label>
              <Form.Control name="aboutBusiness" value={formData.aboutBusiness} onChange={handleChange} />
            </Form.Group>

            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...
                </>
              ) : (
                editingId ? 'Update Client' : 'Save Client'
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default ClientComp;
